import { isDefined } from 'ts-is-present';

import { TO_RICOS_DECORATION_DATA_FIELD } from './../ricos-content/converters/draft/consts';
import type { Decoration, Node, RichContent } from './../ricos-schema/legacy/wix/rich_content/v1';
import { Node_Type } from './../ricos-schema/legacy/wix/rich_content/v1';
import type { TextNode } from './../ricos-types';
import { logInvariant } from './../_shared/log-invariant';

import { UNSUPPORTED_MARK_TYPE, UNSUPPORTED_NODE_TYPE } from './consts';
import type { PmJson, PmMarkConverter, PmMarkJson, PmNodeConverter, PmNodeJson } from './types';

// This collection of methods provides conversion between ProseMirror state JSON and RicosDocument.
// All of the work on JSON version of the ProseMirror state
export type PmConverters = {
  fromPm: (content: PmJson) => RichContent;
  fromPmNode: (node: PmNodeJson) => Node | undefined;
  fromPmNodeChildren: (node: PmJson) => Node[];
  marksToDecorations: (marks: PmMarkJson[]) => Decoration[];

  toPm: (content: RichContent) => PmJson;
  toPmNode: (node: Node) => PmNodeJson;
  toPmNodeChildren: (node: Node) => PmNodeJson[];
  decorationsToMarks: (decorations: Decoration[]) => PmMarkJson[];
};

// Part of BasePluginConfig
type PluginConverters = {
  pmNodeConverters?: PmNodeConverter[];
  pmMarkConverters?: PmMarkConverter[];
};

export const collectPmConverters = (plugins: PluginConverters[]): PmConverters => {
  const markConverters = plugins.flatMap(p => p.pmMarkConverters).filter(isDefined);

  const marksToDecorations: PmConverters['marksToDecorations'] = marks =>
    _marksToDecorations(collectFromPmMarkConverters(markConverters), marks);
  const decorationsToMarks: PmConverters['decorationsToMarks'] = decorations =>
    _decorationsToMarks(collectToPmMarkConverters(markConverters), decorations);

  const allNodeConverters = [
    ...plugins.flatMap(p => p.pmNodeConverters).filter(isDefined),
    createTextConverter({ marksToDecorations, decorationsToMarks }),
  ];
  return {
    ...createFromPmConverters(collectFromPmNodeConverters(allNodeConverters)),
    ...createToPmConverters(collectToPmNodeConverters(allNodeConverters)),
    marksToDecorations,
    decorationsToMarks,
  };
};

type FromPmNodeConverters = Record<
  string,
  ((node: PmNodeJson, visit: (node: PmNodeJson) => Node[]) => Node | undefined)[]
>;

const createFromPmConverters = (
  converters: FromPmNodeConverters
): Pick<PmConverters, 'fromPm' | 'fromPmNode' | 'fromPmNodeChildren'> => {
  const fromPmNodeChildren = (node: { content?: PmNodeJson[] }) =>
    (node.content || []).map(fromPmNode).filter(isDefined);

  const fromPmNode = (node: PmNodeJson): Node | undefined => {
    const convertersArr =
      converters[node.type] === undefined ? [unsupportedFromPm] : converters[node.type];

    return convertersArr.map(convert => convert(node, fromPmNodeChildren)).find(isDefined);
  };

  return {
    fromPmNodeChildren,
    fromPmNode,
    fromPm: (content: PmJson) => ({
      documentStyle: content.attrs?.documentStyle,
      nodes: fromPmNodeChildren(content as PmNodeJson),
    }),
  };
};

const unsupportedFromPm = (node: PmNodeJson): Node | undefined => {
  if (node.type !== UNSUPPORTED_NODE_TYPE) {
    logInvariant(new Error(`Missing converter for node type ${node.type}`));
    return undefined;
  }
  if (!node.attrs?.originalNode) {
    logInvariant(new Error('Unsupported node without original node'));
    return undefined;
  }
  return node.attrs.originalNode;
};

type ToPmNodeConverters = Record<
  string,
  (node: Node, visit: (node: Node) => PmNodeJson[]) => PmNodeJson
>;
const createToPmConverters = (converters: ToPmNodeConverters) => {
  const toPmNodeChildren = (node: Node): PmNodeJson[] => (node.nodes || []).map(toPmNode);

  const toPmNode = (node: Node): PmNodeJson =>
    converters[node.type]
      ? converters[node.type](node, toPmNodeChildren)
      : {
          type: UNSUPPORTED_NODE_TYPE,
          attrs: {
            originalNode: node,
            id: node.id,
          },
        };

  return {
    toPmNodeChildren,
    toPmNode,
    toPm: (content: RichContent): PmJson => {
      const ricosRoot = { id: 'root', type: Node_Type.UNRECOGNIZED, nodes: content.nodes };
      return {
        type: 'doc',
        content: toPmNodeChildren(ricosRoot),
        attrs: {
          documentStyle: content.documentStyle || {},
        },
      };
    },
  };
};

const collectFromPmNodeConverters = (
  proseNodeConverters: PmNodeConverter[]
): FromPmNodeConverters => {
  return proseNodeConverters.reduce<FromPmNodeConverters>((acc, converter) => {
    converter.fromPm.types.forEach(type => {
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(converter.fromPm.convert);
    });
    return acc;
  }, {});
};

type FromPmMarkConverters = Record<string, (mark: PmMarkJson) => Decoration[]>;

const collectFromPmMarkConverters = (
  proseMarkConverters: PmMarkConverter[]
): FromPmMarkConverters =>
  Object.fromEntries<(marks: PmMarkJson) => Decoration[]>(
    proseMarkConverters.map(converter => [converter.fromPm.type, converter.fromPm.convert])
  );

type ToPmMarkConverters = Record<string, ((decorations: Decoration) => PmMarkJson | undefined)[]>;

const collectToPmMarkConverters = (proseMarkConverter: PmMarkConverter[]): ToPmMarkConverters =>
  proseMarkConverter.reduce((acc, value) => {
    acc[value.toPm.type] = acc[value.toPm.type] || [];
    acc[value.toPm.type].push(value.toPm.convert);
    return acc;
  }, {} as Record<string, ((decorations: Decoration) => PmMarkJson | undefined)[]>);

const collectToPmNodeConverters = (proseNodeConverters: PmNodeConverter[]): ToPmNodeConverters =>
  Object.fromEntries<(node: Node) => PmNodeJson>(
    proseNodeConverters.flatMap(converter =>
      converter.toPm.types.map(t => [t, converter.toPm.convert])
    )
  );

const _decorationsToMarks = (
  converters: ToPmMarkConverters,
  decorations: Decoration[]
): PmMarkJson[] => {
  const marks = decorations
    .flatMap((decoration: Decoration): (PmMarkJson | undefined)[] =>
      converters[decoration.type]?.length
        ? converters[decoration.type].map(converter => converter(decoration))
        : [
            {
              type: UNSUPPORTED_MARK_TYPE,
              attrs: { originalDecorationList: [decoration] },
            },
          ]
    )
    .filter(isDefined);
  return mergeUnsupportedMarks(marks);
};

const mergeUnsupportedMarks = (marks: PmMarkJson[]): PmMarkJson[] => {
  return marks.reduce<PmMarkJson[]>((acc, mark) => {
    if (mark.type !== UNSUPPORTED_MARK_TYPE) {
      acc.push(mark);
      return acc;
    }
    const existigUnsupportedMark = acc.find(m => m.type === UNSUPPORTED_MARK_TYPE);
    if (!existigUnsupportedMark) {
      acc.push(mark);
      return acc;
    }
    existigUnsupportedMark.attrs?.originalDecorationList.push(
      ...(mark.attrs?.originalDecorationList || [])
    );
    return acc;
  }, [] as PmMarkJson[]);
};
const _marksToDecorations = (
  converters: FromPmMarkConverters,
  marks: PmMarkJson[]
): Decoration[] => {
  const decorations = marks
    .flatMap((mark: PmMarkJson): Decoration[] =>
      converters[mark.type] ? converters[mark.type](mark) : convertUnsupportedMarkToDecoration(mark)
    )
    .filter(isDefined);
  return mergeSameTypeDecorations(decorations);
};

const mergeSameTypeDecorations = (decorations: Decoration[]): Decoration[] => {
  return decorations
    .sort((a, b) => a.type.localeCompare(b.type))
    .reduce<Decoration[]>((acc, decoration) => {
      const lastDecoration = acc[acc.length - 1];
      const dataKey = (TO_RICOS_DECORATION_DATA_FIELD as $TSFixMe)[decoration.type];
      if (lastDecoration?.type === decoration.type) {
        // @ts-expect-error
        lastDecoration[dataKey as $TSFixMeKey<Decoration>] = {
          ...(lastDecoration[dataKey as $TSFixMeKey<Decoration>] as $TSFixMe),
          ...(decoration[dataKey as $TSFixMeKey<Decoration>] as $TSFixMe),
        };
      } else {
        acc.push(decoration);
      }
      return acc;
    }, []);
};

const convertUnsupportedMarkToDecoration = (mark: PmMarkJson): Decoration[] => {
  if (!mark.attrs?.originalDecorationList) {
    throw new Error(`invalid unsupported mark ${mark.type}`);
  }
  return mark.attrs.originalDecorationList;
};

//  timestamps -----------------------------------------------------------------

const createTextConverter = ({
  marksToDecorations,
  decorationsToMarks,
}: {
  marksToDecorations: (marks: PmMarkJson[]) => Decoration[];
  decorationsToMarks: (decorations: Decoration[]) => PmMarkJson[];
}): PmNodeConverter => ({
  toPm: {
    types: [Node_Type.TEXT],
    // @ts-expect-error $TSFixMe
    convert: (node: TextNode) => {
      if (node.textData.text === '\n') {
        return {
          type: 'hardBreak',
          text: '\n',
          marks: [],
          attrs: {
            id: '',
          },
        };
      }
      return {
        type: 'text',
        text: node.textData.text,
        marks: decorationsToMarks(node.textData.decorations ?? []),
        attrs: {
          id: '',
        },
      };
    },
  },
  fromPm: {
    types: ['hardBreak', 'text'],
    convert: (node: PmNodeJson) => {
      if (node.type === 'hardBreak') {
        return {
          type: Node_Type.TEXT,
          id: '',
          nodes: [],
          textData: {
            text: '\n',
            decorations: [],
          },
        };
      }
      return {
        type: Node_Type.TEXT,
        id: '',
        nodes: [],
        textData: {
          text: node.text || '',
          decorations: marksToDecorations(node.marks || []),
        },
      };
    },
  },
});
